import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, ChevronDown, ChevronUp, Plus, Minus, RefreshCw, Eye, ArrowRight } from "lucide-react";
import { useWizard } from "@/context/WizardContext";
import { diffAgainstSnapshot, staleFieldsOnRecert } from "@/lib/payloadDiff";
import { fieldToStep, fieldToTestId } from "@/lib/fieldToStep";

const SOURCE_KEY = "wi-pa-copy-source-v1";

function tryReadSource() {
  try {
    const raw = typeof window === "undefined" ? null : window.localStorage.getItem(SOURCE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Summarises what the nurse has changed vs. the snapshot we seeded from via
 * Copy-from-last-visit. Renders only when a copy source exists — otherwise
 * a fresh intake produces no diff and the component is invisible.
 */
export default function RecertDiffCard() {
  const { data } = useWizard();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  const source = useMemo(() => tryReadSource(), []);
  const diff = useMemo(
    () => (source ? diffAgainstSnapshot(data, source.snapshot) : null),
    [data, source]
  );
  const stale = useMemo(
    () => (source ? staleFieldsOnRecert(data, source.snapshot) : []),
    [data, source]
  );

  if (!source || !diff) return null;

  const jumpToStale = (field) => {
    const target = fieldToStep(field);
    if (!target) return;
    try {
      sessionStorage.setItem(
        "wizard_focus_field",
        JSON.stringify({ field, step: target.step, ts: Date.now() })
      );
    } catch { /* non-fatal */ }
    navigate(`/wizard/${target.step}`);
  };

  const hoursAgo = Math.max(1, Math.round((Date.now() - (source.copied_at || Date.now())) / 3_600_000));
  const ago = hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.round(hoursAgo / 24)}d ago`;
  const total = diff.totalChanges;

  return (
    <section
      className="relative mb-6 rounded-2xl bg-white border border-[#E7EEF3] overflow-hidden"
      style={{ boxShadow: "0 4px 16px rgba(11,30,58,0.06), 0 1px 3px rgba(11,30,58,0.04)" }}
      data-testid="recert-diff-card"
    >
      {/* Left accent rail — flat yellow */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: "#FFDA0B" }}
        aria-hidden="true"
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-4 pl-6 pr-5 py-4 text-left"
        data-testid="recert-diff-toggle"
      >
        <div className="w-11 h-11 rounded-2xl bg-[#FFDA0B] flex items-center justify-center shrink-0">
          <History className="w-5 h-5 text-[#072A54]" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8A6B00]">Recert · changes vs last visit</div>
          <div className="font-display text-lg font-semibold text-[#0B1E3A] mt-0.5 truncate tracking-tight">
            {total === 0
              ? `No changes yet · seeded from ${source.client_name || "previous visit"}, ${ago}`
              : `${total} update${total === 1 ? "" : "s"} since ${source.client_name || "last visit"}`}
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-[#3A5275]" />
        ) : (
          <ChevronDown className="w-4 h-4 text-[#3A5275]" />
        )}
      </button>

      {expanded && (total > 0 || stale.length > 0) && (
        <div className="pl-6 pr-5 pb-5 space-y-3 text-[13.5px] text-[#0B1E3A]" data-testid="recert-diff-body">
          {diff.diagnoses.added.length + diff.diagnoses.removed.length > 0 && (
            <GroupRow
              label="Diagnoses"
              added={diff.diagnoses.added.map((d) => `${d.icd10 || "—"} ${d.description || ""}`.trim())}
              removed={diff.diagnoses.removed.map((d) => `${d.icd10 || "—"} ${d.description || ""}`.trim())}
              testid="recert-diff-diagnoses"
            />
          )}

          {diff.medications.added.length + diff.medications.removed.length + diff.medications.changed.length > 0 && (
            <GroupRow
              label="Medications"
              added={diff.medications.added.map((m) => `${m.name} · ${m.dose || "?"} ${m.frequency || ""}`.trim())}
              removed={diff.medications.removed.map((m) => m.name)}
              changed={diff.medications.changed.map(({ before, after }) =>
                `${after.name}: ${before.dose || "?"} ${before.frequency || ""} → ${after.dose || "?"} ${after.frequency || ""}`
              )}
              testid="recert-diff-medications"
            />
          )}

          {diff.adls.length > 0 && (
            <GroupRow
              label="ADL levels"
              changed={diff.adls.map(({ key, before, after }) => `${cap(key)}: ${before || "?"} → ${after || "?"}`)}
              testid="recert-diff-adls"
            />
          )}

          {diff.fields.length > 0 && (
            <div data-testid="recert-diff-fields">
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#3A5275] mb-1">
                Field edits
              </div>
              <ul className="space-y-1.5">
                {diff.fields.map((f) => (
                  <li key={f.path} className="flex items-start gap-2">
                    <RefreshCw className="w-3.5 h-3.5 text-[#1FA0E6] mt-[3px] shrink-0" />
                    <div className="min-w-0">
                      <span className="font-semibold">{f.label}:</span>{" "}
                      <span className="line-through text-[#8b9bb0]">{diff.truncate(f.before) || "—"}</span>
                      {" → "}
                      <span className="text-[#0A3D79] font-semibold">{diff.truncate(f.after) || "—"}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stale.length > 0 && (
            <div
              className="mt-2 pt-3 border-t border-[rgba(11,30,58,0.08)]"
              data-testid="recert-stale-section"
            >
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-[#a07a27]" />
                <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#a07a27]">
                  Re-review suggested · unchanged since last visit
                </div>
              </div>
              <p className="text-[12.5px] text-[#3A5275] mb-2 leading-snug">
                These narrative fields are identical to the previous submission. Rubber-stamp recerts sometimes get rejected — confirm each one still reflects today's visit.
              </p>
              <ul className="space-y-1.5">
                {stale.map((f) => (
                  <li
                    key={f.path}
                    className="flex items-start gap-3 rounded-lg bg-[#fbf2d8]/40 border border-[#DBB90A]/40 px-3 py-2"
                    data-testid={`recert-stale-${f.path}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#0B1E3A]">{f.label}</div>
                      <div className="text-[11.5px] text-[#3A5275] mt-0.5 truncate">{f.sample}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => jumpToStale(f.path)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md border border-[#DBB90A] bg-white hover:bg-[#fbf2d8] text-[#a07a27] text-[11.5px] font-semibold px-2.5 py-1 transition"
                      data-testid={`recert-stale-${f.path}-jump`}
                    >
                      Review <ArrowRight className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function cap(s) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function GroupRow({ label, added = [], removed = [], changed = [], testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#3A5275] mb-1">{label}</div>
      <ul className="space-y-1.5">
        {added.map((t) => (
          <li key={`a-${t}`} className="flex items-start gap-2">
            <Plus className="w-3.5 h-3.5 text-[#0A3D79] mt-[3px] shrink-0" />
            <span className="text-[#0B1E3A]">{t}</span>
          </li>
        ))}
        {changed.map((t) => (
          <li key={`c-${t}`} className="flex items-start gap-2">
            <RefreshCw className="w-3.5 h-3.5 text-[#1FA0E6] mt-[3px] shrink-0" />
            <span className="text-[#0B1E3A]">{t}</span>
          </li>
        ))}
        {removed.map((t) => (
          <li key={`r-${t}`} className="flex items-start gap-2">
            <Minus className="w-3.5 h-3.5 text-[#c05746] mt-[3px] shrink-0" />
            <span className="text-[#8b9bb0] line-through">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
